import { A11yModule } from '@angular/cdk/a11y';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AstralButtonComponent } from '../astral-button/astral-button.component';
import { AstralDialogBackgroundInertService } from './astral-dialog-background-inert.service';
import { AstralDialogScrollLockService } from './astral-dialog-scroll-lock.service';

export type AstralDialogRole = 'dialog' | 'alertdialog';
export type AstralDialogSize = 'small' | 'medium' | 'large';
export type AstralDialogVariant = 'modal' | 'prompt' | 'news';
export type AstralNewsDialogEmphasis = 'standard' | 'urgent';

@Component({
  selector: 'astral-dialog',
  standalone: true,
  imports: [A11yModule, AstralButtonComponent, NgTemplateOutlet, TranslatePipe],
  templateUrl: './astral-dialog.component.html',
  styleUrls: ['./astral-dialog.component.scss'],
})
export class AstralDialogComponent implements OnChanges, OnDestroy {
  private static nextId = 0;
  readonly titleId = `astral-dialog-title-${++AstralDialogComponent.nextId}`;
  readonly contentId = `${this.titleId}-content`;
  private ownsBackgroundInert = false;
  private ownsScrollLock = false;
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly backgroundInert: AstralDialogBackgroundInertService,
    private readonly scrollLock: AstralDialogScrollLockService
  ) {}

  @Input() ariaLabel: string;
  @Input() ariaLabelKey: string;
  @Input() closeOnBackdrop = false;
  @Input() closeOnEscape = true;
  @Input() open = false;
  @Input() newsEmphasis: AstralNewsDialogEmphasis = 'standard';
  @Input() preserveNewsAspectRatio = false;
  @Input() role: AstralDialogRole | null = null;
  @Input() showCloseButton = true;
  @Input() size: AstralDialogSize = 'medium';
  /** Keeps a stacked dialog visible while a newer dialog owns interaction and focus. */
  @Input() suspended = false;
  @Input() title: string;
  @Input() titleKey: string;
  @Input() variant: AstralDialogVariant = 'modal';

  @Output() closed = new EventEmitter<void>();

  get hasTitle(): boolean {
    return !!this.resolvedTitle;
  }

  get resolvedAriaLabel(): string {
    return this.ariaLabelKey ?? this.ariaLabel ?? 'ATTENTION_TEXT';
  }

  get resolvedTitle(): string {
    return this.titleKey ?? this.title ?? '';
  }

  get resolvedRole(): AstralDialogRole {
    return this.role ?? (this.variant === 'prompt' ? 'alertdialog' : 'dialog');
  }

  get closeIcon(): string {
    return this.variant === 'news' ? 'closeAlternate' : 'clear';
  }

  ngOnChanges(changes: SimpleChanges): void {
    const openChange = changes['open'];
    if (openChange?.currentValue && !openChange.previousValue) {
      const activeElement = this.document.activeElement;
      this.previouslyFocusedElement = activeElement instanceof HTMLElement ? activeElement : null;
      this.acquireScrollLock();
    }

    if (this.open && (openChange || changes['suspended'])) {
      this.registerBackgroundInert();
    }

    if (openChange && !openChange.currentValue && openChange.previousValue) {
      this.unregisterBackgroundInert();
      this.releaseScrollLock();
      this.restorePreviouslyFocusedElement();
    }
  }

  ngOnDestroy(): void {
    this.unregisterBackgroundInert();
    this.releaseScrollLock();
    this.restorePreviouslyFocusedElement();
  }

  handleBackdropClick(event: MouseEvent): void {
    if (!this.suspended && this.closeOnBackdrop && event.target === event.currentTarget) {
      this.requestClose();
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (!this.suspended && event.key === 'Escape' && this.closeOnEscape) {
      event.preventDefault();
      this.requestClose();
    }
  }

  requestClose(): void {
    this.closed.emit();
  }

  private registerBackgroundInert(): void {
    this.backgroundInert.register(this.elementRef.nativeElement, this.suspended);
    this.ownsBackgroundInert = true;
  }

  private unregisterBackgroundInert(): void {
    if (this.ownsBackgroundInert) {
      this.backgroundInert.unregister(this.elementRef.nativeElement);
      this.ownsBackgroundInert = false;
    }
  }

  private acquireScrollLock(): void {
    if (!this.ownsScrollLock) {
      this.scrollLock.acquire();
      this.ownsScrollLock = true;
    }
  }

  private releaseScrollLock(): void {
    if (this.ownsScrollLock) {
      this.scrollLock.release();
      this.ownsScrollLock = false;
    }
  }

  private restorePreviouslyFocusedElement(): void {
    const elementToRestore = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    queueMicrotask(() => {
      if (elementToRestore?.isConnected) {
        elementToRestore.focus();
      }
    });
  }
}
